import React, { useState, useEffect, useRef, FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Doctor, PortalMode } from '../../types/api';
import { apiService } from '../../services/api';
import { mapStoredUserToDoctor, parseStoredUser } from '../../utils/doctorUser';
import { getApiErrorMessage } from '../../utils/apiError';
import { AspenHealthLogo } from '../../components/AspenHealthLogo';
import { startHqSso, hqSsoLogoutUrl, consumeHqSignedOut } from '../../services/hqSso';
// import SingpassLoginButton from '../../components/SingpassLoginButton';

interface LoginPageProps {
  onLogin: (doctor: Doctor, needsSignature: boolean, portal?: PortalMode) => void;
}

function LoginPage({ onLogin }: LoginPageProps) {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [singpassLoading, setSingpassLoading] = useState<boolean>(false);
  const singpassResumeHandled = useRef(false);
  const [searchParams] = useSearchParams();
  const [signedOut] = useState(consumeHqSignedOut);
  const ssoFlag = searchParams.get('sso');
  const skipSilentSso = signedOut || ssoFlag === 'required' || ssoFlag === 'error' || ssoFlag === 'used';
  const ssoErrorMessage = searchParams.get('msg') || (
    ssoFlag === 'used'
      ? 'This sign-in link was already used. Start again from the login page.'
      : ssoFlag === 'error'
        ? 'HQ sign-in did not finish. Try again or sign out of HQ SSO.'
        : ''
  );

  useEffect(() => {
    if (skipSilentSso) return;
    void startHqSso({ prompt: 'none' });
  }, [skipSilentSso]);

  useEffect(() => {
    if (singpassResumeHandled.current) return;
    if (sessionStorage.getItem('drTele_postSingpassDoLogin') !== '1') return;
    singpassResumeHandled.current = true;
    sessionStorage.removeItem('drTele_postSingpassDoLogin');
    const u = parseStoredUser();
    if (u && localStorage.getItem('accessToken')) {
      onLogin(mapStoredUserToDoctor(u), true, 'doctor');
    }
  }, [onLogin]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data: response, portal } = await apiService.loginDoctorOrStaff({ username, password });

      // Verify token was stored
      const storedToken = localStorage.getItem('accessToken');
      console.log('Login successful - Token stored:', !!storedToken);
      if (storedToken) {
        // Decode token to verify it has the correct claims
        try {
          const tokenParts = storedToken.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            console.log('Token sub claim:', payload.sub);
            console.log('Token role_id claim:', payload.role_id);
            console.log('Token expires:', new Date(payload.exp * 1000));
          }
        } catch (e) {
          console.error('Error decoding token after login:', e);
        }
      }

      // Map User to Doctor (including FormattedSignatureLine fields from CheckLine)
      const doctor: Doctor = {
        id: response.user.id,
        name: response.user.fullName,
        mcr: response.user.mcr || '',
        displayName: response.user.displayName || response.user.fullName,
        customText1: response.user.customText1 || '',
        customText2: response.user.customText2 || '',
        maskOn: response.user.maskOn || false,
        code: response.user.code || '',
        username: response.user.username || username,
        roleId: response.user.roleId,
      };

      const needsSignature = portal === 'doctor';

      onLogin(doctor, needsSignature, portal);
    } catch (err: unknown) {
      console.error('Login error:', err);
      setError(getApiErrorMessage(err, 'Invalid username or password.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSingpass = async () => {
    setError('');
    setSingpassLoading(true);
    try {
      const authUrl = await apiService.getDoctorSingpassAuthUrl();
      window.location.href = authUrl;
    } catch (err: unknown) {
      console.error('Singpass login error:', err);
      setError(getApiErrorMessage(err, 'Unable to start Singpass login. Please try again.'));
      setSingpassLoading(false);
    }
  };

  if (!skipSilentSso) {
    return (
      <div className="login-container">
        <div className="login-box">
          <p>Checking HQ sign-inâ€¦</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <AspenHealthLogo variant="login" showPortalLabel />
        <p className="login-subtitle">Doctor or staff â€” same login; staff opens the waiting-room chat queue.</p>
        {ssoErrorMessage && (
          <div style={{ color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 14 }}>
            <div>{ssoErrorMessage}</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-primary" onClick={() => { void startHqSso({ prompt: 'login' }); }}>
                Try again
              </button>
              <button type="button" className="btn btn-primary" style={{ background: '#475569', borderColor: '#475569' }} onClick={() => { window.location.assign(hqSsoLogoutUrl()); }}>
                Sign out of HQ SSO
              </button>
            </div>
          </div>
        )}

        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter username"
              autoComplete="username"
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              autoComplete="current-password"
            />
          </div>
          {error && (
            <div style={{ color: '#f87171', marginBottom: 16, fontSize: 14 }}>{error}</div>
          )}
          <button type="submit" className="btn btn-primary" disabled={loading || singpassLoading}>
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>
        <button
          type="button"
          className="btn btn-primary"
          style={{ marginTop: 12, background: '#144937', borderColor: '#144937' }}
          disabled={loading || singpassLoading}
          onClick={() => { void startHqSso(ssoErrorMessage ? { prompt: 'login' } : undefined); }}
        >
          HQ sign-in
        </button>

        {/* Singpass login temporarily hidden
        <div style={{ marginTop: 20 }}>
          <SingpassLoginButton
            variant="red"
            disabled={loading}
            loading={singpassLoading}
            loadingLabel="Redirecting to Singpassâ€¦"
            onClick={() => void handleSingpass()}
          />
        </div>
        */}
      </div>
    </div>
  );
}

export default LoginPage;
